import { Table, TableColumn } from '@backstage/core-components';
import { Team, VulnerabilityItem } from '@internal/schemas-common';
import {
  isCVEGroup,
  isPackageGroup,
  isProjectGroup,
} from '../utils/groupLevelHelpers';
import { orange, red } from '@material-ui/core/colors';
import { useEffect, useRef } from 'react';

import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import { customFilterAndSearch } from '../utils/customFilterAndSearch';
import { getPackageLevelSeverityCounts } from '../utils/getPackageLevelSeverityCounts';
import { getProjectLevelSeverityCounts } from '../utils/getProjectLevelSeverityCounts';
import { getVulnerabilityScore } from '../utils/getVulnerabilitiesScore';
import { useGetVulnerabilities } from '../hooks/useGetVulnerabilities';

const columns: TableColumn[] = [
  {
    title: 'Project',
    type: 'string',
    field: 'project',
    defaultGroupOrder: 0,
  },
  {
    title: 'Package',
    type: 'string',
    field: 'resource',
    customSort: (vulnerabilityA: any, vulnerabilityB: any) =>
      vulnerabilityA.score > vulnerabilityB.score ? -1 : 1,
    defaultGroupOrder: 1,
  },
  {
    title: 'ID',
    type: 'string',
    field: 'id',
    customSort: (vulnerabilityA: any, vulnerabilityB: any) =>
      vulnerabilityA.score > vulnerabilityB.score ? -1 : 1,
    defaultGroupOrder: 2,
  },
  {
    title: 'Image',
    type: 'string',
    field: 'image',
    align: 'center',
    sorting: false,
  },
  {
    title: 'Vulnerable Package',
    type: 'string',
    align: 'center',
    sorting: false,
    render: (vulnerability: any): React.ReactNode => (
      <>
        {vulnerability.packageType?.length ? (
          <Chip
            label={`Type: ${vulnerability.packageType}`}
            size="small"
            variant="default"
          />
        ) : undefined}
        {vulnerability.packagePath?.length ? (
          <Chip
            label={`Path: ${vulnerability.packagePath}`}
            size="small"
            variant="default"
          />
        ) : undefined}
      </>
    ),
    customFilterAndSearch: (filter: string, vulnerability: any) =>
      customFilterAndSearch(filter, vulnerability, [
        'packageType',
        'packagePath',
        'resource',
        'title',
        'severity',
        'score',
      ]),
  },
  {
    title: 'Deployment Details',
    type: 'string',
    sorting: false,
    align: 'center',
    render: (vulnerability: any): React.ReactNode => (
      <>
        <Chip
          label={`Cluster: ${vulnerability.cluster}`}
          size="small"
          variant="default"
        />
        <Chip
          label={`Namespace: ${vulnerability.ref.namespace}`}
          size="small"
          variant="default"
        />
        <Chip
          label={vulnerability.ref.resourceKind}
          size="small"
          variant="default"
        />
        <Chip
          label={vulnerability.ref.resourceName}
          size="small"
          variant="default"
        />
      </>
    ),
    customFilterAndSearch: (filter: string, vulnerability: any) =>
      customFilterAndSearch(filter, vulnerability.ref, [
        'env',
        'namespace',
        'resourceKind',
        'resourceName',
      ]),
  },
  {
    title: 'Package Version',
    type: 'string',
    align: 'center',
    sorting: false,
    render: (vulnerability: any): React.ReactNode => (
      <>
        <Chip
          label={`Installed: ${vulnerability.installedVersion}`}
          size="small"
          variant="default"
        />
        <Chip
          label={
            vulnerability.fixedVersion === 'Not Yet Fixed'
              ? vulnerability.fixedVersion
              : `Fixed: ${vulnerability.fixedVersion}`
          }
          size="small"
          variant="default"
        />
      </>
    ),
    customFilterAndSearch: (filter: string, vulnerability: any) =>
      customFilterAndSearch(filter, vulnerability, [
        'installedVersion',
        'fixedVersion',
      ]),
  },
];

export const VulnerabilitiesTable = ({
  title,
  teams,
  updateScoreCb,
  updateLoadingCb,
}: {
  title?: string;
  teams: Team[];
  updateScoreCb: any;
  updateLoadingCb: any;
}) => {
  const foundVulnerabilities = useRef<VulnerabilityItem[]>([]);
  const { vulnerabilities, loading } = useGetVulnerabilities({
    teams,
  });
  useEffect(() => {
    if (vulnerabilities?.length) {
      foundVulnerabilities.current = vulnerabilities;
      const score = getVulnerabilityScore(vulnerabilities);
      updateScoreCb(score);
    }
    if (!loading) {
      updateLoadingCb(loading);
    }
  }, [vulnerabilities, updateScoreCb, loading, updateLoadingCb]);

  return (
    vulnerabilities && (
      <Table
        options={{
          paging: true,
          padding: 'dense',
          sorting: true,
          pageSize: 15,
          groupRowSeparator: ' ',
          groupTitle: group => {
            // Format titles as per group type
            if (isCVEGroup(group)) {
              return (
                <>
                  <Button
                    target="_blank"
                    href={group.data[0].primaryLink}
                    size="medium"
                    color="primary"
                    style={{ width: '150px' }}
                  >
                    {group.value}
                  </Button>
                  <Chip
                    label={group.data[0].severity}
                    size="small"
                    variant="outlined"
                    style={{
                      color:
                        group.data[0].severity === 'CRITICAL'
                          ? red[400]
                          : orange[400],
                      width: '80px',
                    }}
                  />
                  <Chip
                    label={group.data[0].score}
                    size="small"
                    variant="default"
                  />
                  {group.data[0].title}
                </>
              );
            } else if (isPackageGroup(group)) {
              const { criticalCount, highCount } =
                getPackageLevelSeverityCounts(group);
              return (
                <>
                  <Chip
                    label={`CRITICAL: ${criticalCount}`}
                    size="medium"
                    variant="outlined"
                    style={{ color: red[400], width: '140px' }}
                  />
                  <Chip
                    label={`HIGH: ${highCount}`}
                    size="medium"
                    variant="outlined"
                    style={{ color: orange[400] }}
                  />
                  {'Package: '}
                </>
              );
            } else if (isProjectGroup(group)) {
              const { criticalCount, highCount } =
                getProjectLevelSeverityCounts(group);
              return (
                <>
                  <Chip
                    label={`CRITICAL: ${criticalCount}`}
                    size="medium"
                    variant="outlined"
                    style={{ color: red[400], width: '140px' }}
                  />
                  <Chip
                    label={`HIGH: ${highCount}`}
                    size="medium"
                    variant="outlined"
                    style={{ color: orange[400], width: '120px' }}
                  />
                  {'Project: '}
                </>
              );
            }
            return group.value;
          },
        }}
        data={vulnerabilities}
        columns={columns}
        isLoading={loading}
        title={title ?? 'Vulnerabilities'}
      />
    )
  );
};
